/**
 * Paginated InComm cardholders (incomm_demo) with filters.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/common/Badge';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import type { Column } from '@/components/common/Table';
import { Pagination, Table } from '@/components/common/Table';
import type { IncommCardListItem, IncommCardListResponse, IncommCardListParams } from '@/app/services/incommCardService';
import { fetchIncommCardList } from '@/app/services/incommCardService';
import {
  cardStatusBadgeVariant,
  formatCardStatusLabel,
  INCOMM_CARD_STATUS_FILTER_OPTIONS,
} from '@/pages/IncommCards/cardStatusBadgeVariant';
import { INCOMM_LIST_QUERY_UNSET } from '@/pages/IncommCards/incommListNavigation';

import styles from './IncommCardsList.module.scss';

const PAGE_SIZE = 50;

type FiltersState = {
  memberId: string;
  cardStatus: string;
  cardStatusUnset: boolean;
  partnerProductId: string;
  search: string;
};

const emptyFilters = (): FiltersState => ({
  memberId: '',
  cardStatus: '',
  cardStatusUnset: false,
  partnerProductId: '',
  search: '',
});

function filtersFromSearchParams(sp: URLSearchParams): FiltersState {
  const unsetRaw = sp.get(INCOMM_LIST_QUERY_UNSET);
  const cardStatusUnset =
    unsetRaw === '1' ||
    unsetRaw === 'true' ||
    sp.get('card_status_is_null') === 'true';
  return {
    memberId: sp.get('member_id') ?? sp.get('eligible_participant_id') ?? '',
    cardStatus: cardStatusUnset ? '' : (sp.get('card_status') ?? ''),
    cardStatusUnset,
    partnerProductId: sp.get('partner_product_id') ?? '',
    search: sp.get('search') ?? '',
  };
}

function parsePositiveInt(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : undefined;
}

function buildListParams(page: number, applied: FiltersState): IncommCardListParams {
  const partner_product_id = parsePositiveInt(applied.partnerProductId);
  const params: IncommCardListParams = {
    page,
    page_size: PAGE_SIZE,
  };
  if (applied.memberId.trim() !== '') {
    params.member_id = applied.memberId.trim();
  }
  if (applied.cardStatusUnset) {
    params.card_status_is_null = true;
  } else if (applied.cardStatus.trim() !== '') {
    params.card_status = applied.cardStatus.trim();
  }
  if (partner_product_id !== undefined) {
    params.partner_product_id = partner_product_id;
  }
  if (applied.search.trim() !== '') {
    params.search = applied.search.trim();
  }
  return params;
}

function formatMoney(amount: string | null): string {
  if (amount == null) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(isoOrDate: string | null): string {
  if (isoOrDate == null) return '—';
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return isoOrDate;
  return d.toLocaleString();
}

export const IncommCardsList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [appliedFilters, setAppliedFilters] = useState<FiltersState>(() =>
    filtersFromSearchParams(searchParams)
  );
  const [draftFilters, setDraftFilters] = useState<FiltersState>(() =>
    filtersFromSearchParams(searchParams)
  );
  const [page, setPage] = useState(1);
  const searchParamsStringRef = useRef(searchParams.toString());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<IncommCardListResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildListParams(page, appliedFilters);
      const response = await fetchIncommCardList(params);
      setData(response);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    const s = searchParams.toString();
    if (searchParamsStringRef.current === s) {
      return;
    }
    searchParamsStringRef.current = s;
    const next = filtersFromSearchParams(searchParams);
    setAppliedFilters(next);
    setDraftFilters(next);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = useMemo(() => {
    if (data == null || data.total <= 0) return 0;
    return Math.ceil(data.total / data.page_size);
  }, [data]);

  const handleApplyFilters = useCallback(() => {
    const applied = { ...draftFilters };
    setAppliedFilters(applied);
    setPage(1);
    const nx = new URLSearchParams();
    if (applied.cardStatusUnset) {
      nx.set(INCOMM_LIST_QUERY_UNSET, '1');
    } else if (applied.cardStatus.trim() !== '') {
      nx.set('card_status', applied.cardStatus.trim());
    }
    if (applied.memberId.trim() !== '') {
      nx.set('member_id', applied.memberId.trim());
    }
    if (applied.partnerProductId.trim() !== '') {
      nx.set('partner_product_id', applied.partnerProductId.trim());
    }
    if (applied.search.trim() !== '') {
      nx.set('search', applied.search.trim());
    }
    const nextStr = nx.toString();
    searchParamsStringRef.current = nextStr;
    setSearchParams(nx, { replace: true });
  }, [draftFilters, setSearchParams]);

  const handleResetFilters = useCallback(() => {
    const cleared = emptyFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
    searchParamsStringRef.current = '';
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApplyFilters();
      }
    },
    [handleApplyFilters]
  );

  const columns = useMemo((): Column<IncommCardListItem>[] => {
    return [
      {
        key: 'eligible_participant_id',
        header: 'Participant',
        width: '8rem',
        render: (row) => (
          <Link
            className={styles.participantLink}
            to={`/incomm-cards/${row.eligible_participant_id}`}
          >
            {row.eligible_participant_id}
          </Link>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        render: (row) =>
          [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || '—',
      },
      {
        key: 'email',
        header: 'Email',
        render: (row) => row.email ?? '—',
      },
      {
        key: 'card_status',
        header: 'Card status',
        render: (row) => (
          <Badge variant={cardStatusBadgeVariant(row.card_status)} size="sm">
            {formatCardStatusLabel(row.card_status)}
          </Badge>
        ),
      },
      {
        key: 'card_last4',
        header: 'Last4',
        width: '5rem',
        render: (row) => row.card_last4 ?? '—',
      },
      {
        key: 'wallet_balance',
        header: 'Wallet',
        align: 'right',
        render: (row) => formatMoney(row.wallet_balance),
      },
      {
        key: 'shipped_date',
        header: 'Shipped',
        render: (row) => formatDate(row.shipped_date),
      },
      {
        key: 'partner_name',
        header: 'Partner',
        render: (row) => (
          <>
            {row.partner_name ?? row.doing_business_as ?? '—'}
            {row.partner_product_id != null && (
              <span className={styles.mutedSuffix}> (#{row.partner_product_id})</span>
            )}
          </>
        ),
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (row) => formatDate(row.created_at),
      },
    ];
  }, []);

  const showEmpty = !loading && error == null && data != null && data.items.length === 0;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>All InComm cardholders</h1>
          <p className={styles.pageDescription}>
            Filter by member ID (Lore participant ID or InComm external member ID), card status,
            partner product, or search (member number / group number). Encrypted name and email are
            not searchable server-side.
          </p>
        </div>
        <a className={styles.toolbarLink} href="#/incomm-cards">
          Back to summary
        </a>
      </header>

      <div className="filters-section" onKeyPress={handleKeyPress}>
        <div className="filters-content">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="incomm-filter-member-id">Member ID</label>
              <input
                id="incomm-filter-member-id"
                type="text"
                placeholder="Participant ID or InComm member ID…"
                value={draftFilters.memberId}
                onChange={(ev) =>
                  setDraftFilters((f) => ({ ...f, memberId: ev.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label htmlFor="incomm-filter-status">Card status</label>
              <select
                id="incomm-filter-status"
                value={draftFilters.cardStatus}
                disabled={draftFilters.cardStatusUnset}
                onChange={(ev) => setDraftFilters((f) => ({ ...f, cardStatus: ev.target.value }))}
              >
                {INCOMM_CARD_STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt === '' ? '_any' : opt} value={opt}>
                    {opt === '' ? 'Any' : opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group filter-groupCheckbox">
              <label htmlFor="incomm-filter-unset-only">
                <input
                  id="incomm-filter-unset-only"
                  type="checkbox"
                  checked={draftFilters.cardStatusUnset}
                  onChange={(ev) =>
                    setDraftFilters((f) => ({
                      ...f,
                      cardStatusUnset: ev.target.checked,
                      cardStatus: ev.target.checked ? '' : f.cardStatus,
                    }))
                  }
                />{' '}
                Not sent to InComm yet only (no card status)
              </label>
            </div>
            <div className="filter-group">
              <label htmlFor="incomm-filter-partner-product">Partner product ID</label>
              <input
                id="incomm-filter-partner-product"
                type="text"
                inputMode="numeric"
                placeholder="Product ID…"
                value={draftFilters.partnerProductId}
                onChange={(ev) =>
                  setDraftFilters((f) => ({ ...f, partnerProductId: ev.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label htmlFor="incomm-filter-search">Search</label>
              <input
                id="incomm-filter-search"
                type="text"
                placeholder="Member no. or group no.…"
                value={draftFilters.search}
                onChange={(ev) => setDraftFilters((f) => ({ ...f, search: ev.target.value }))}
              />
            </div>
            <div className="filter-actions">
              <button type="button" className="btn-primary" onClick={handleApplyFilters}>
                Apply filters
              </button>
              <button type="button" className="btn-secondary" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className={styles.loading}>Loading cardholders…</div>}

      {error != null && (
        <div className={styles.errorMessage} role="alert">
          Failed to load cardholders: {error}
        </div>
      )}

      {!loading && error == null && data != null && (
        <div className={styles.resultsBlock}>
          <Card>
            <CardHeader title="Cardholders" subtitle={`${data.total} total matching filters`} />
            <CardBody>
              {showEmpty ? (
                <p className={styles.pageDescription}>No cardholders found.</p>
              ) : (
                <>
                  <Table
                    columns={columns}
                    data={data.items}
                    keyExtractor={(row) => String(row.eligible_participant_id)}
                    emptyMessage="No cardholders found."
                  />
                  <Pagination
                    currentPage={data.page}
                    totalPages={totalPages}
                    totalItems={data.total}
                    itemsPerPage={data.page_size}
                    onPageChange={(p) => setPage(p)}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
};

export default IncommCardsList;
